import React, { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { Brain, AlertTriangle, Activity, Heart, Shield, Briefcase, Wind, ChevronRight, ChevronLeft, Play, Pause, Volume2, Award, CheckCircle, XCircle, Home, BookOpen, GraduationCap, Menu, X, Globe, User, LogIn, LogOut, Download } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping
const IconMap = {
  brain: Brain,
  "alert-triangle": AlertTriangle,
  activity: Activity,
  heart: Heart,
  shield: Shield,
  briefcase: Briefcase,
  wind: Wind
};

// Color mapping
const ColorMap = {
  blue: "from-blue-500 to-blue-600",
  orange: "from-orange-500 to-orange-600",
  red: "from-red-500 to-red-600",
  green: "from-green-500 to-green-600",
  purple: "from-purple-500 to-purple-600",
  teal: "from-teal-500 to-teal-600",
  cyan: "from-cyan-500 to-cyan-600"
};

const ColorBg = {
  blue: "bg-blue-100 text-blue-600",
  orange: "bg-orange-100 text-orange-600",
  red: "bg-red-100 text-red-600",
  green: "bg-green-100 text-green-600",
  purple: "bg-purple-100 text-purple-600",
  teal: "bg-teal-100 text-teal-600",
  cyan: "bg-cyan-100 text-cyan-600"
};

// Language flags
const LanguageFlags = {
  fr: "🇫🇷",
  en: "🇬🇧",
  ar: "🇸🇦",
  es: "🇪🇸"
};

const LanguageNames = {
  fr: "Français",
  en: "English",
  ar: "العربية",
  es: "Español"
};

// Arabic numerals conversion
const toArabicNumerals = (num) => {
  const arabicNumerals = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  return String(num).replace(/[0-9]/g, (d) => arabicNumerals[parseInt(d)]);
};

// Breathing Exercise Component
const BreathingExercise = ({ t, language }) => {
  const [isRunning, setIsRunning] = useState(false);
  const [phase, setPhase] = useState("ready"); // ready, inhale, hold, exhale
  const [counter, setCounter] = useState(0);
  const [cycles, setCycles] = useState(0);
  const intervalRef = useRef(null);

  const phases = {
    inhale: { duration: 4, next: "hold" },
    hold: { duration: 4, next: "exhale" },
    exhale: { duration: 6, next: "inhale" }
  };

  const phaseLabels = {
    fr: { inhale: "Inspirez", hold: "Retenez", exhale: "Expirez", ready: "Prêt" },
    en: { inhale: "Inhale", hold: "Hold", exhale: "Exhale", ready: "Ready" },
    ar: { inhale: "شهيق", hold: "احتفظ", exhale: "زفير", ready: "مستعد" },
    es: { inhale: "Inhala", hold: "Mantén", exhale: "Exhala", ready: "Listo" }
  };

  const startExercise = () => {
    setIsRunning(true);
    setPhase("inhale");
    setCounter(phases.inhale.duration);
    setCycles(0);
  };

  const stopExercise = () => {
    setIsRunning(false);
    setPhase("ready");
    setCounter(0);
    if (intervalRef.current) clearInterval(intervalRef.current);
  };

  useEffect(() => {
    if (isRunning && phase !== "ready") {
      intervalRef.current = setInterval(() => {
        setCounter(prev => {
          if (prev <= 1) {
            const nextPhase = phases[phase].next;
            setPhase(nextPhase);
            if (nextPhase === "inhale") {
              setCycles(c => c + 1);
            }
            return phases[nextPhase].duration;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isRunning, phase]);

  useEffect(() => {
    if (cycles >= 5) {
      stopExercise();
    }
  }, [cycles]);

  const getCircleSize = () => {
    if (phase === "inhale") return "scale-100";
    if (phase === "hold") return "scale-100";
    if (phase === "exhale") return "scale-75";
    return "scale-75";
  };

  const labels = phaseLabels[language] || phaseLabels.fr;

  return (
    <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-8 text-center" data-testid="breathing-exercise">
      <h3 className="text-2xl font-bold mb-6 text-cyan-800">{t("breathing_exercise")}</h3>
      
      <div className="relative w-64 h-64 mx-auto mb-6">
        <div className={`absolute inset-0 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 transition-transform duration-1000 ease-in-out ${getCircleSize()} flex items-center justify-center`}>
          <div className="text-white text-center">
            <div className="text-4xl font-bold">{phase !== "ready" ? counter : ""}</div>
            <div className="text-xl mt-2">{labels[phase]}</div>
          </div>
        </div>
        {isRunning && (
          <svg className="absolute inset-0 w-full h-full -rotate-90">
            <circle
              cx="128"
              cy="128"
              r="120"
              fill="none"
              stroke="rgba(255,255,255,0.3)"
              strokeWidth="8"
            />
            <circle
              cx="128"
              cy="128"
              r="120"
              fill="none"
              stroke="white"
              strokeWidth="8"
              strokeDasharray={`${2 * Math.PI * 120}`}
              strokeDashoffset={`${2 * Math.PI * 120 * (1 - counter / phases[phase]?.duration || 0)}`}
              className="transition-all duration-1000"
            />
          </svg>
        )}
      </div>

      <div className="mb-4 text-gray-600">
        {isRunning ? `Cycle ${cycles + 1}/5` : t("start_exercise")}
      </div>

      <button
        onClick={isRunning ? stopExercise : startExercise}
        className={`px-8 py-3 rounded-full font-semibold transition ${
          isRunning
            ? "bg-red-500 text-white hover:bg-red-600"
            : "bg-cyan-500 text-white hover:bg-cyan-600"
        }`}
        data-testid="breathing-btn"
      >
        {isRunning ? t("stop_exercise") : t("start_exercise")}
      </button>
    </div>
  );
};

// Visualization Exercise Component
const VisualizationExercise = ({ t, language }) => {
  const [currentScene, setCurrentScene] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const scenes = {
    fr: [
      { emoji: "🏖️", title: "Plage Tropicale", description: "Imaginez-vous sur une plage de sable blanc. Entendez les vagues, sentez la brise chaude, voyez le coucher de soleil orange et rose..." },
      { emoji: "🌲", title: "Forêt Paisible", description: "Vous êtes dans une forêt calme. Écoutez le chant des oiseaux, sentez l'odeur des pins, marchez sur le tapis de feuilles..." },
      { emoji: "🏔️", title: "Sommet de Montagne", description: "Au sommet d'une montagne, vous contemplez une vue panoramique. L'air pur remplit vos poumons, vous vous sentez libre..." },
      { emoji: "🌸", title: "Jardin Fleuri", description: "Dans un jardin au printemps, entouré de fleurs colorées. Les papillons dansent, un doux parfum flotte dans l'air..." }
    ],
    en: [
      { emoji: "🏖️", title: "Tropical Beach", description: "Imagine yourself on a white sand beach. Hear the waves, feel the warm breeze, see the orange and pink sunset..." },
      { emoji: "🌲", title: "Peaceful Forest", description: "You are in a calm forest. Listen to birdsong, smell the pines, walk on the carpet of leaves..." },
      { emoji: "🏔️", title: "Mountain Peak", description: "At the top of a mountain, you contemplate a panoramic view. Pure air fills your lungs, you feel free..." },
      { emoji: "🌸", title: "Flower Garden", description: "In a spring garden, surrounded by colorful flowers. Butterflies dance, a sweet scent floats in the air..." }
    ],
    ar: [
      { emoji: "🏖️", title: "شاطئ استوائي", description: "تخيل نفسك على شاطئ رملي أبيض. اسمع الأمواج، اشعر بالنسيم الدافئ، شاهد غروب الشمس البرتقالي والوردي..." },
      { emoji: "🌲", title: "غابة هادئة", description: "أنت في غابة هادئة. استمع إلى تغريد الطيور، استنشق رائحة الصنوبر، امشِ على سجادة الأوراق..." },
      { emoji: "🏔️", title: "قمة الجبل", description: "على قمة الجبل، تتأمل منظراً بانورامياً. الهواء النقي يملأ رئتيك، تشعر بالحرية..." },
      { emoji: "🌸", title: "حديقة الزهور", description: "في حديقة ربيعية، محاط بزهور ملونة. الفراشات ترقص، رائحة حلوة تطفو في الهواء..." }
    ],
    es: [
      { emoji: "🏖️", title: "Playa Tropical", description: "Imagínate en una playa de arena blanca. Escucha las olas, siente la brisa cálida, mira la puesta de sol naranja y rosa..." },
      { emoji: "🌲", title: "Bosque Tranquilo", description: "Estás en un bosque tranquilo. Escucha el canto de los pájaros, huele los pinos, camina sobre la alfombra de hojas..." },
      { emoji: "🏔️", title: "Cima de Montaña", description: "En la cima de una montaña, contemplas una vista panorámica. El aire puro llena tus pulmones, te sientes libre..." },
      { emoji: "🌸", title: "Jardín Florido", description: "En un jardín primaveral, rodeado de flores coloridas. Las mariposas bailan, un dulce aroma flota en el aire..." }
    ]
  };

  const currentScenes = scenes[language] || scenes.fr;

  const nextScene = () => {
    stopAudio(); // Stop audio when changing scene
    setCurrentScene((prev) => (prev + 1) % currentScenes.length);
  };

  const prevScene = () => {
    stopAudio(); // Stop audio when changing scene
    setCurrentScene((prev) => (prev - 1 + currentScenes.length) % currentScenes.length);
  };

  const playAudio = () => {
    const text = currentScenes[currentScene].description;
    
    // Cancel any ongoing speech first
    window.speechSynthesis.cancel();
    
    // Create utterance
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = language === "ar" ? "ar-SA" : language === "es" ? "es-ES" : language === "en" ? "en-US" : "fr-FR";
    utterance.rate = 0.8;
    utterance.pitch = 1;
    utterance.volume = 1;
    
    utterance.onstart = () => setIsPlaying(true);
    utterance.onend = () => setIsPlaying(false);
    utterance.onerror = () => setIsPlaying(false);
    
    // Set playing state immediately for UI feedback
    setIsPlaying(true);
    
    // Small delay to ensure cancel is processed, then speak
    setTimeout(() => {
      window.speechSynthesis.speak(utterance);
    }, 100);
  };

  const stopAudio = () => {
    window.speechSynthesis.cancel();
    setIsPlaying(false);
  };

  return (
    <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8" data-testid="visualization-exercise">
      <h3 className="text-2xl font-bold mb-6 text-purple-800 text-center">{t("visualization_exercise")}</h3>
      
      <div className="bg-white rounded-xl p-6 shadow-lg mb-6">
        <div className="text-6xl text-center mb-4">{currentScenes[currentScene].emoji}</div>
        <h4 className="text-xl font-semibold text-center mb-4 text-purple-700">
          {currentScenes[currentScene].title}
        </h4>
        <p className="text-gray-600 text-center leading-relaxed">
          {currentScenes[currentScene].description}
        </p>
      </div>

      <div className="flex justify-center items-center gap-4 mb-4">
        <button
          onClick={prevScene}
          className="p-2 rounded-full bg-purple-100 text-purple-600 hover:bg-purple-200"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        
        <button
          onClick={isPlaying ? stopAudio : playAudio}
          className={`px-6 py-3 rounded-full font-semibold flex items-center gap-2 ${
            isPlaying
              ? "bg-red-500 text-white hover:bg-red-600"
              : "bg-purple-500 text-white hover:bg-purple-600"
          }`}
        >
          {isPlaying ? <Pause className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          {isPlaying ? t("stop_exercise") : t("listen")}
        </button>
        
        <button
          onClick={nextScene}
          className="p-2 rounded-full bg-purple-100 text-purple-600 hover:bg-purple-200"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>

      <div className="flex justify-center gap-2">
        {currentScenes.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentScene(idx)}
            className={`w-3 h-3 rounded-full transition ${
              idx === currentScene ? "bg-purple-500" : "bg-purple-200"
            }`}
          />
        ))}
      </div>
    </div>
  );
};

// Certificate Component
const CertificateModal = ({ certificate, module, user, t, onClose }) => {
  const certificateRef = useRef(null);

  const downloadCertificate = () => {
    const element = certificateRef.current;
    if (!element) return;

    // Create a printable version
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Certificate - ${module?.title}</title>
          <style>
            body { margin: 0; padding: 20px; font-family: 'Georgia', serif; }
            .certificate { 
              width: 800px; 
              margin: 0 auto; 
              padding: 40px; 
              border: 8px double #7c3aed; 
              background: linear-gradient(135deg, #faf5ff 0%, #f5f3ff 100%);
            }
            .header { text-align: center; margin-bottom: 30px; }
            .logo { font-size: 48px; margin-bottom: 10px; }
            .title { font-size: 42px; color: #7c3aed; margin: 30px 0; font-weight: bold; text-align: center; letter-spacing: 4px; }
            .subtitle { font-size: 18px; color: #6b7280; }
            .content { text-align: center; margin: 40px 0; }
            .awarded-to { font-size: 16px; color: #6b7280; margin-bottom: 10px; }
            .name { font-size: 32px; color: #1f2937; font-weight: bold; margin-bottom: 20px; }
            .module { font-size: 24px; color: #7c3aed; margin-bottom: 10px; }
            .footer { 
              display: flex; 
              justify-content: space-between; 
              margin-top: 60px; 
              padding-top: 20px; 
              border-top: 1px solid #e5e7eb; 
            }
            .signature { text-align: center; }
            .signature-img { 
              font-family: 'Brush Script MT', cursive; 
              font-size: 28px; 
              color: #1f2937;
              margin-bottom: 5px;
            }
            .signature-line { width: 150px; border-top: 1px solid #1f2937; margin: 5px auto; }
            .verify { text-align: center; margin-top: 30px; font-size: 12px; color: #9ca3af; }
          </style>
        </head>
        <body>
          <div class="certificate">
            <div class="header">
              <img src="https://customer-assets.emergentagent.com/job_french-lexilearn/artifacts/ftk31l6l_ChatGPT%20Image%205%20janv.%202026%2C%2022_55_05.png" alt="Logo" style="width: 60px; height: 60px; border-radius: 8px; object-fit: cover; margin-bottom: 10px;" />
              <div style="font-size: 24px; font-weight: bold;">Mental Health Training Course</div>
              <div class="subtitle">${t("app_subtitle")}</div>
            </div>
            <div class="title">${t("certificate_title")}</div>
            <div class="content">
              <div class="awarded-to">${t("certificate_awarded_to")}</div>
              <div class="name">${user?.name || "Participant"}</div>
              <div style="color: #6b7280; margin-bottom: 20px;">${t("certificate_completion")}</div>
              <div class="module">${module?.title || "Module"}</div>
            </div>
            <div class="footer">
              <div class="signature">
                <div class="signature-line"></div>
                <div>${t("certificate_director")}</div>
              </div>
              <div style="text-align: center;">
                <div style="font-size: 48px;">🏆</div>
              </div>
              <div class="signature">
                <div class="signature-line"></div>
                <div>${t("certificate_date")}: ${new Date(certificate.issued_at).toLocaleDateString()}</div>
              </div>
            </div>
            <div class="verify">
              ${t("certificate_verify")}: ${certificate.certificate_number}
            </div>
          </div>
          <script>window.onload = function() { window.print(); }</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="certificate-modal">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold">{t("certificate_title")}</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Certificate Preview */}
          <div 
            ref={certificateRef}
            className="border-8 border-double border-purple-500 rounded-lg p-8 bg-gradient-to-br from-purple-50 to-indigo-50 mb-6"
          >
            <div className="text-center mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_french-lexilearn/artifacts/ftk31l6l_ChatGPT%20Image%205%20janv.%202026%2C%2022_55_05.png" 
                alt="Logo" 
                className="w-16 h-16 rounded-lg object-cover mx-auto mb-2"
              />
              <div className="text-2xl font-bold text-gray-800">Mental Health Training Course</div>
              <div className="text-gray-500">{t("app_subtitle")}</div>
            </div>

            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-purple-600 mb-6 tracking-widest">{t("certificate_title")}</h1>
              <p className="text-gray-600 mb-2">{t("certificate_awarded_to")}</p>
              <p className="text-3xl font-bold text-gray-800 mb-4">{user?.name || "Participant"}</p>
              <p className="text-gray-600 mb-2">{t("certificate_completion")}</p>
              <p className="text-2xl font-semibold text-purple-600">{module?.title || "Module"}</p>
            </div>

            <div className="flex justify-between items-end mt-12 pt-6 border-t border-gray-200">
              <div className="text-center">
                <div className="w-32 border-t border-gray-400 mb-2 mx-auto"></div>
                <p className="text-sm text-gray-600">{t("certificate_director")}</p>
              </div>
              <div className="text-5xl">🏆</div>
              <div className="text-center">
                <div className="w-32 border-t border-gray-400 mb-2 mx-auto"></div>
                <p className="text-sm text-gray-600">{t("certificate_date")}: {new Date(certificate.issued_at).toLocaleDateString()}</p>
              </div>
            </div>

            <p className="text-center text-xs text-gray-400 mt-6">
              {t("certificate_verify")}: {certificate.certificate_number}
            </p>
          </div>

          <button
            onClick={downloadCertificate}
            className="w-full py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 flex items-center justify-center gap-2"
            data-testid="download-certificate-btn"
          >
            <Download className="w-5 h-5" />
            {t("download_certificate")}
          </button>
        </div>
      </div>
    </div>
  );
};

function App() {
  const [currentPage, setCurrentPage] = useState("home");
  const [language, setLanguage] = useState("fr");
  const [translations, setTranslations] = useState({});
  const [modules, setModules] = useState([]);
  const [currentModule, setCurrentModule] = useState(null);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [currentLessonIndex, setCurrentLessonIndex] = useState(0);
  const [quiz, setQuiz] = useState([]);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [user, setUser] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  const [authName, setAuthName] = useState("");
  const [authEmail, setAuthEmail] = useState("");
  const [userProgress, setUserProgress] = useState([]);
  const [lessonProgress, setLessonProgress] = useState([]);
  const [stats, setStats] = useState({});
  const [isPlaying, setIsPlaying] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [certificates, setCertificates] = useState([]);
  const [showCertificate, setShowCertificate] = useState(null);
  const audioRef = useRef(null);

  // Fetch translations
  useEffect(() => {
    const fetchTranslations = async () => {
      try {
        const res = await axios.get(`${API}/translations/${language}`);
        setTranslations(res.data);
      } catch (e) {
        console.error("Error fetching translations", e);
      }
    };
    fetchTranslations();
  }, [language]);

  // Fetch modules
  useEffect(() => {
    const fetchModules = async () => {
      try {
        const res = await axios.get(`${API}/modules/${language}`);
        setModules(res.data);
      } catch (e) {
        console.error("Error fetching modules", e);
      }
    };
    fetchModules();
  }, [language]);

  // Fetch stats
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API}/stats`);
        setStats(res.data);
      } catch (e) {
        console.error("Error fetching stats", e);
      }
    };
    fetchStats();
  }, []);

  // Fetch user progress
  useEffect(() => {
    if (user) {
      const fetchProgress = async () => {
        try {
          const [progressRes, lessonRes, certRes] = await Promise.all([
            axios.get(`${API}/progress/${user.id}`),
            axios.get(`${API}/progress/${user.id}/lessons`),
            axios.get(`${API}/certificates/${user.id}`)
          ]);
          setUserProgress(progressRes.data);
          setLessonProgress(lessonRes.data);
          setCertificates(certRes.data);
        } catch (e) {
          console.error("Error fetching progress", e);
        }
      };
      fetchProgress();
    }
  }, [user]);

  const t = useCallback((key) => translations[key] || key, [translations]);

  // Function to format numbers based on language
  const formatNumber = useCallback((num) => {
    if (language === 'ar') {
      return toArabicNumerals(num);
    }
    return num;
  }, [language]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/users`, {
        name: authName,
        email: authEmail,
        language: language
      });
      setUser(res.data);
      setShowAuth(false);
      localStorage.setItem("stress_user", JSON.stringify(res.data));
    } catch (e) {
      console.error("Error creating user", e);
    }
  };

  const handleLogout = () => {
    setUser(null);
    setUserProgress([]);
    setLessonProgress([]);
    setCertificates([]);
    setCurrentPage("home");
    localStorage.removeItem("stress_user");
    setAuthName("");
    setAuthEmail("");
  };

  // Check for existing user
  useEffect(() => {
    const savedUser = localStorage.getItem("stress_user");
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
  }, []);

  // Reload current module when language changes
  useEffect(() => {
    const reloadCurrentModule = async () => {
      if (currentModule && (currentPage === "lesson" || currentPage === "quiz")) {
        try {
          const res = await axios.get(`${API}/modules/${language}/${currentModule.id}`);
          setCurrentModule(res.data);
          // Keep the same lesson index but update content
          if (res.data.lessons[currentLessonIndex]) {
            setCurrentLesson(res.data.lessons[currentLessonIndex]);
          }
          // If on quiz page, reload quiz
          if (currentPage === "quiz") {
            const quizRes = await axios.get(`${API}/modules/${language}/${currentModule.id}/quiz`);
            setQuiz(quizRes.data);
          }
        } catch (e) {
          console.error("Error reloading module for language change", e);
        }
      }
    };
    reloadCurrentModule();
  }, [language]); // eslint-disable-line react-hooks/exhaustive-deps

  const openModule = async (moduleId) => {
    try {
      const res = await axios.get(`${API}/modules/${language}/${moduleId}`);
      setCurrentModule(res.data);
      setCurrentLessonIndex(0);
      setCurrentLesson(res.data.lessons[0]);
      setCurrentPage("lesson");
    } catch (e) {
      console.error("Error fetching module", e);
    }
  };

  const navigateLesson = (direction) => {
    if (!currentModule) return;
    const newIndex = currentLessonIndex + direction;
    if (newIndex >= 0 && newIndex < currentModule.lessons.length) {
      setCurrentLessonIndex(newIndex);
      setCurrentLesson(currentModule.lessons[newIndex]);
    }
  };

  const markLessonComplete = async () => {
    if (!user || !currentModule || !currentLesson) return;
    try {
      await axios.post(`${API}/progress/lesson?user_id=${user.id}&module_id=${currentModule.id}&lesson_id=${currentLesson.id}`);
      const res = await axios.get(`${API}/progress/${user.id}/lessons`);
      setLessonProgress(res.data);
    } catch (e) {
      console.error("Error marking lesson complete", e);
    }
  };

  const openQuiz = async () => {
    if (!currentModule) return;
    try {
      const res = await axios.get(`${API}/modules/${language}/${currentModule.id}/quiz`);
      setQuiz(res.data);
      setQuizAnswers({});
      setQuizResult(null);
      setCurrentPage("quiz");
    } catch (e) {
      console.error("Error fetching quiz", e);
    }
  };

  const submitQuiz = async () => {
    if (!user || !currentModule) return;
    try {
      const res = await axios.post(`${API}/quiz/submit`, {
        user_id: user.id,
        module_id: currentModule.id,
        answers: quizAnswers
      });
      setQuizResult(res.data);
      const progressRes = await axios.get(`${API}/progress/${user.id}`);
      setUserProgress(progressRes.data);
    } catch (e) {
      console.error("Error submitting quiz", e);
    }
  };

  const playAudio = async (text) => {
    try {
      setIsPlaying(true);
      const cleanText = text.replace(/<[^>]*>/g, " ").substring(0, 1000);
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(cleanText);
      const langMap = { fr: "fr-FR", en: "en-US", ar: "ar-SA", es: "es-ES" };
      utterance.lang = langMap[language] || "fr-FR";
      utterance.rate = 0.9;
      utterance.onend = () => setIsPlaying(false);
      utterance.onerror = () => setIsPlaying(false);
      audioRef.current = { cancel: () => window.speechSynthesis.cancel() };
      window.speechSynthesis.speak(utterance);
    } catch (e) {
      console.error("Error playing audio", e);
      setIsPlaying(false);
    }
  };

  const stopAudio = () => {
    window.speechSynthesis.cancel();
    setIsPlaying(false);
  };

  const generateCertificate = async (moduleId) => {
    if (!user) return;
    try {
      const res = await axios.post(`${API}/certificate`, {
        user_id: user.id,
        module_id: moduleId
      });
      setCertificates([...certificates, res.data]);
      const module = modules.find(m => m.id === moduleId);
      setShowCertificate({ certificate: res.data, module });
    } catch (e) {
      console.error("Error generating certificate", e);
      alert(t("quiz_failed"));
    }
  };

  const isModulePassed = (moduleId) => {
    return userProgress.some(p => p.module_id === moduleId && p.passed);
  };

  const hasCertificate = (moduleId) => {
    return certificates.some(c => c.module_id === moduleId);
  };

  const getModuleProgress = (moduleId) => {
    const moduleLessons = modules.find(m => m.id === moduleId);
    if (!moduleLessons) return 0;
    const completed = lessonProgress.filter(p => p.module_id === moduleId && p.completed).length;
    return Math.round((completed / moduleLessons.lesson_count) * 100);
  };

  // Determine text direction for Arabic
  const isRTL = language === "ar";

  // Header component
  const Header = () => (
    <header className="bg-white shadow-sm sticky top-0 z-50" data-testid="header" dir={isRTL ? "rtl" : "ltr"}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center cursor-pointer" onClick={() => setCurrentPage("home")} data-testid="logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_french-lexilearn/artifacts/ftk31l6l_ChatGPT%20Image%205%20janv.%202026%2C%2022_55_05.png" 
              alt={t("app_title")} 
              className="w-10 h-10 rounded-lg object-cover"
            />
            <span className={`${isRTL ? 'mr-2' : 'ml-2'} text-xl font-bold text-gray-900 hidden sm:block`}>{t("app_title")}</span>
          </div>

          <nav className="hidden md:flex items-center space-x-6">
            <button onClick={() => setCurrentPage("home")} className={`flex items-center space-x-1 ${currentPage === "home" ? "text-purple-600" : "text-gray-600 hover:text-purple-600"}`} data-testid="nav-home">
              <Home className="w-4 h-4" />
              <span>{t("home")}</span>
            </button>
            <button onClick={() => setCurrentPage("modules")} className={`flex items-center space-x-1 ${currentPage === "modules" ? "text-purple-600" : "text-gray-600 hover:text-purple-600"}`} data-testid="nav-modules">
              <BookOpen className="w-4 h-4" />
              <span>{t("modules")}</span>
            </button>
            {user && (
              <button onClick={() => setCurrentPage("progress")} className={`flex items-center space-x-1 ${currentPage === "progress" ? "text-purple-600" : "text-gray-600 hover:text-purple-600"}`} data-testid="nav-progress">
                <GraduationCap className="w-4 h-4" />
                <span>{t("my_progress")}</span>
              </button>
            )}
          </nav>

          <div className="flex items-center space-x-4">
            <div className="relative group">
              <button className="flex items-center space-x-1 px-3 py-2 rounded-lg hover:bg-gray-100" data-testid="language-selector">
                <Globe className="w-4 h-4" />
                <span>{LanguageFlags[language]}</span>
              </button>
              <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                {Object.entries(LanguageNames).map(([code, name]) => (
                  <button key={code} onClick={() => setLanguage(code)} className={`w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center space-x-2 ${language === code ? "bg-purple-50 text-purple-600" : ""}`} data-testid={`lang-${code}`}>
                    <span>{LanguageFlags[code]}</span>
                    <span>{name}</span>
                  </button>
                ))}
              </div>
            </div>

            {user ? (
              <div className="flex items-center space-x-2">
                <div className="flex items-center space-x-2 px-3 py-2 bg-purple-50 rounded-lg" data-testid="user-info">
                  <User className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-medium text-purple-600 hidden sm:block">{user.name}</span>
                </div>
                <button 
                  onClick={handleLogout} 
                  className="flex items-center space-x-1 px-3 py-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  data-testid="logout-btn"
                  title={t("logout")}
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden sm:block text-sm">{t("logout")}</span>
                </button>
              </div>
            ) : (
              <button onClick={() => setShowAuth(true)} className="flex items-center space-x-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700" data-testid="login-btn">
                <LogIn className="w-4 h-4" />
                <span className="hidden sm:block">{t("login")}</span>
              </button>
            )}

            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t" data-testid="mobile-menu">
            <button onClick={() => { setCurrentPage("home"); setMobileMenuOpen(false); }} className="block w-full text-left px-4 py-2 hover:bg-gray-100">{t("home")}</button>
            <button onClick={() => { setCurrentPage("modules"); setMobileMenuOpen(false); }} className="block w-full text-left px-4 py-2 hover:bg-gray-100">{t("modules")}</button>
            {user && <button onClick={() => { setCurrentPage("progress"); setMobileMenuOpen(false); }} className="block w-full text-left px-4 py-2 hover:bg-gray-100">{t("my_progress")}</button>}
            {user && (
              <button 
                onClick={() => { handleLogout(); setMobileMenuOpen(false); }} 
                className="block w-full text-left px-4 py-2 hover:bg-red-50 text-red-600"
              >
                {t("logout")}
              </button>
            )}
          </div>
        )}
      </div>
    </header>
  );

  // Auth Modal is rendered directly in JSX to avoid focus issues

  // Home Page
  const HomePage = () => (
    <div data-testid="home-page" dir={isRTL ? "rtl" : "ltr"}>
      <section className="bg-gradient-to-br from-purple-900 via-purple-800 to-blue-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="inline-block px-4 py-1 bg-purple-700 rounded-full text-sm mb-4">{t("certification_available")}</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">{t("app_title")}</h1>
            <p className="text-xl text-purple-200 mb-8 max-w-3xl mx-auto">{t("app_subtitle")}</p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <button onClick={() => user ? setCurrentPage("modules") : setShowAuth(true)} className="px-8 py-3 bg-white text-purple-900 rounded-lg font-semibold hover:bg-gray-100 transition" data-testid="start-learning-btn">{t("start_learning")}</button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center"><div className="text-3xl font-bold text-purple-600">{formatNumber(stats.modules || 7)}</div><div className="text-gray-600">{t("modules")}</div></div>
            <div className="text-center"><div className="text-3xl font-bold text-purple-600">{formatNumber(stats.lessons || 20)}</div><div className="text-gray-600">{t("lessons")}</div></div>
            <div className="text-center"><div className="text-3xl font-bold text-purple-600">{formatNumber(stats.quiz_questions || 35)}</div><div className="text-gray-600">{t("quiz_questions")}</div></div>
            <div className="text-center"><div className="text-3xl font-bold text-purple-600">{formatNumber(4)}</div><div className="text-gray-600">{t("languages")}</div></div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">{t("features_title")}</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4"><BookOpen className="w-6 h-6 text-purple-600" /></div>
              <h3 className="text-xl font-semibold mb-2">{t("feature_modules")}</h3>
              <p className="text-gray-600">{t("feature_modules_desc")}</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4"><Volume2 className="w-6 h-6 text-green-600" /></div>
              <h3 className="text-xl font-semibold mb-2">{t("feature_audio")}</h3>
              <p className="text-gray-600">{t("feature_audio_desc")}</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-4"><Award className="w-6 h-6 text-yellow-600" /></div>
              <h3 className="text-xl font-semibold mb-2">{t("feature_certificates")}</h3>
              <p className="text-gray-600">{t("feature_certificates_desc")}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-4">{t("our_modules")}</h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">{t("our_modules_desc")}</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {modules.slice(0, 6).map((module) => {
              const Icon = IconMap[module.icon] || Brain;
              return (
                <div key={module.id} className="bg-white rounded-xl shadow-sm border hover:shadow-lg transition cursor-pointer overflow-hidden" onClick={() => user ? openModule(module.id) : setShowAuth(true)} data-testid={`module-card-${module.id}`}>
                  <div className={`h-2 bg-gradient-to-r ${ColorMap[module.color]}`}></div>
                  <div className="p-6">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${ColorBg[module.color]}`}><Icon className="w-6 h-6" /></div>
                    <h3 className="text-lg font-semibold mb-2">{module.title}</h3>
                    <p className="text-gray-600 text-sm mb-4">{module.description}</p>
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <span>{formatNumber(module.lesson_count)} {t("lessons")}</span>
                      <span>{formatNumber(module.quiz_count)} {t("questions")}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">{t("select_language")}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto">
            {Object.entries(LanguageNames).map(([code, name]) => (
              <button key={code} onClick={() => setLanguage(code)} className={`p-6 rounded-xl text-center transition ${language === code ? "bg-purple-600 text-white" : "bg-white hover:bg-purple-50 border"}`} data-testid={`language-btn-${code}`}>
                <div className="text-4xl mb-2">{LanguageFlags[code]}</div>
                <div className="font-semibold">{name}</div>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white" data-testid="donation-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-pink-100 rounded-full mb-4">
              <Heart className="w-8 h-8 text-pink-500" />
            </div>
            <h2 className="text-3xl font-bold mb-4">{t("donate_title")}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto mb-8">{t("donate_desc")}</p>
            <div className="flex justify-center">
              <stripe-buy-button
                buy-button-id="buy_btn_1SmS4Q6mAPKif3wgqI80jRHR"
                publishable-key="pk_live_51Sjs9c6mAPKif3wgCitebYZurclLYJPlOX3VBJFpOpfQgIiX5GsRaLv01yS0D2NQT4PvoM5sWQLAunnzCguI2BE80017xYpyHY"
              >
              </stripe-buy-button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-r from-purple-600 to-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">{t("ready_start")}</h2>
          <p className="text-purple-200 mb-8">{t("ready_start_desc")}</p>
          <button onClick={() => user ? setCurrentPage("modules") : setShowAuth(true)} className="px-8 py-3 bg-white text-purple-600 rounded-lg font-semibold hover:bg-gray-100">{t("start_free")}</button>
        </div>
      </section>
    </div>
  );

  // Modules Page
  const ModulesPage = () => (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="modules-page" dir={isRTL ? "rtl" : "ltr"}>
      <h1 className="text-3xl font-bold mb-8">{t("modules")}</h1>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {modules.map((module) => {
          const Icon = IconMap[module.icon] || Brain;
          const progress = getModuleProgress(module.id);
          const passed = isModulePassed(module.id);
          const hasCert = hasCertificate(module.id);

          return (
            <div key={module.id} className="bg-white rounded-xl shadow-sm border hover:shadow-lg transition overflow-hidden" data-testid={`module-${module.id}`}>
              <div className={`h-2 bg-gradient-to-r ${ColorMap[module.color]}`}></div>
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${ColorBg[module.color]}`}><Icon className="w-6 h-6" /></div>
                  {passed && <span className="px-2 py-1 bg-green-100 text-green-600 text-xs rounded-full flex items-center"><CheckCircle className="w-3 h-3 mr-1" />{t("completed")}</span>}
                </div>
                <h3 className="text-lg font-semibold mb-2">{module.title}</h3>
                <p className="text-gray-600 text-sm mb-4">{module.description}</p>
                
                {user && (
                  <div className="mb-4">
                    <div className="flex justify-between text-sm text-gray-500 mb-1"><span>{t("progression")}</span><span>{formatNumber(progress)}%</span></div>
                    <div className="h-2 bg-gray-200 rounded-full"><div className={`h-2 rounded-full bg-gradient-to-r ${ColorMap[module.color]}`} style={{ width: `${progress}%` }}></div></div>
                  </div>
                )}

                <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                  <span>{formatNumber(module.lesson_count)} {t("lessons")}</span>
                  <span>{formatNumber(module.quiz_count)} {t("questions")}</span>
                </div>

                <div className="flex gap-2">
                  <button onClick={() => openModule(module.id)} className={`flex-1 py-2 rounded-lg font-medium transition bg-gradient-to-r ${ColorMap[module.color]} text-white hover:opacity-90`}>
                    {progress > 0 ? t("continue") : t("start_learning")}
                  </button>
                  {passed && !hasCert && (
                    <button onClick={() => generateCertificate(module.id)} className="px-3 py-2 bg-yellow-100 text-yellow-600 rounded-lg hover:bg-yellow-200" title={t("get_certificate")}><Award className="w-5 h-5" /></button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // Lesson Page
  const LessonPage = () => {
    if (!currentModule || !currentLesson) return null;
    const Icon = IconMap[currentModule.icon] || Brain;
    const isExerciseModule = currentModule.is_exercise;

    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="lesson-page" dir={isRTL ? "rtl" : "ltr"}>
        <div className="flex items-center space-x-2 text-sm text-gray-500 mb-6">
          <button onClick={() => setCurrentPage("modules")} className="hover:text-purple-600">{t("modules")}</button>
          <ChevronRight className="w-4 h-4" />
          <span className="text-gray-900 font-medium">{currentModule.title}</span>
        </div>

        <div className={`bg-gradient-to-r ${ColorMap[currentModule.color]} rounded-xl p-6 text-white mb-6`}>
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center"><Icon className="w-6 h-6" /></div>
            <div>
              <h1 className="text-2xl font-bold">{currentModule.title}</h1>
              <p className="text-white text-opacity-80">{t("lessons")} {currentLessonIndex + 1} / {currentModule.lessons.length}</p>
            </div>
          </div>
          <div className="h-2 bg-white bg-opacity-30 rounded-full">
            <div className="h-2 bg-white rounded-full transition-all" style={{ width: `${((currentLessonIndex + 1) / currentModule.lessons.length) * 100}%` }}></div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">{currentLesson.title}</h2>
            <button onClick={() => isPlaying ? stopAudio() : playAudio(currentLesson.content)} className={`p-3 rounded-full transition ${isPlaying ? "bg-red-100 text-red-600" : "bg-purple-100 text-purple-600 hover:bg-purple-200"}`} data-testid="audio-btn">
              {isPlaying ? <Pause className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </button>
          </div>
          <div className="prose prose-lg max-w-none lesson-content" dangerouslySetInnerHTML={{ __html: currentLesson.content }}></div>
          
          {/* Interactive Exercises for Module 7 */}
          {isExerciseModule && currentLesson.id === "lesson-7-1" && (
            <div className="mt-8">
              <BreathingExercise t={t} language={language} />
            </div>
          )}
          {isExerciseModule && currentLesson.id === "lesson-7-2" && (
            <div className="mt-8">
              <VisualizationExercise t={t} language={language} />
            </div>
          )}
        </div>

        <div className="flex items-center justify-between">
          <button onClick={() => navigateLesson(-1)} disabled={currentLessonIndex === 0} className={`flex items-center space-x-2 px-4 py-2 rounded-lg ${currentLessonIndex === 0 ? "text-gray-400 cursor-not-allowed" : "text-gray-600 hover:bg-gray-100"}`} data-testid="prev-lesson-btn">
            <ChevronLeft className="w-5 h-5" />
            <span>{t("previous_lesson")}</span>
          </button>

          {currentLessonIndex === currentModule.lessons.length - 1 ? (
            <button onClick={() => { markLessonComplete(); openQuiz(); }} className="flex items-center space-x-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700" data-testid="take-quiz-btn">
              <span>{t("take_quiz")}</span>
              <ChevronRight className="w-5 h-5" />
            </button>
          ) : (
            <button onClick={() => { markLessonComplete(); navigateLesson(1); }} className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700" data-testid="next-lesson-btn">
              <span>{t("next_lesson")}</span>
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    );
  };

  // Quiz Page
  const QuizPage = () => {
    if (!currentModule || quiz.length === 0) return null;

    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="quiz-page" dir={isRTL ? "rtl" : "ltr"}>
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Quiz: {currentModule.title}</h1>
          <p className="text-gray-600">{t("quiz_questions")}: {formatNumber(quiz.length)} | Min: {formatNumber(70)}%</p>
        </div>

        {quizResult && (
          <div className={`p-6 rounded-xl mb-8 ${quizResult.passed ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`} data-testid="quiz-result">
            <div className="flex items-center space-x-4">
              {quizResult.passed ? <CheckCircle className="w-12 h-12 text-green-500" /> : <XCircle className="w-12 h-12 text-red-500" />}
              <div>
                <h3 className={`text-xl font-bold ${quizResult.passed ? "text-green-700" : "text-red-700"}`}>{quizResult.passed ? t("quiz_passed") : t("quiz_failed")}</h3>
                <p className="text-gray-600">{t("score")}: {formatNumber(quizResult.score)}/{formatNumber(quizResult.total)} ({formatNumber(quizResult.percentage.toFixed(0))}%)</p>
              </div>
            </div>
            {quizResult.passed && (
              <div className="mt-4 flex gap-3">
                <button onClick={() => setCurrentPage("modules")} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">{t("return_modules")}</button>
                {!hasCertificate(currentModule.id) && (
                  <button onClick={() => generateCertificate(currentModule.id)} className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 flex items-center space-x-2">
                    <Award className="w-4 h-4" />
                    <span>{t("get_certificate")}</span>
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {!quizResult && (
          <>
            <div className="space-y-6">
              {quiz.map((q, index) => (
                <div key={q.id} className="bg-white rounded-xl shadow-sm border p-6" data-testid={`question-${q.id}`}>
                  <h3 className="text-lg font-medium mb-4"><span className="text-purple-600 mr-2">{index + 1}.</span>{q.question}</h3>
                  <div className="space-y-3">
                    {q.options.map((option, optIndex) => (
                      <label key={optIndex} className={`flex items-center p-3 rounded-lg border cursor-pointer transition ${quizAnswers[q.id] === optIndex ? "border-purple-500 bg-purple-50" : "border-gray-200 hover:border-purple-300"}`}>
                        <input type="radio" name={q.id} checked={quizAnswers[q.id] === optIndex} onChange={() => setQuizAnswers({ ...quizAnswers, [q.id]: optIndex })} className="w-4 h-4 text-purple-600" />
                        <span className="ml-3">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 flex justify-between">
              <button onClick={() => setCurrentPage("lesson")} className="px-6 py-3 border rounded-lg hover:bg-gray-100">{t("return_lessons")}</button>
              <button onClick={submitQuiz} disabled={Object.keys(quizAnswers).length < quiz.length} className={`px-6 py-3 rounded-lg font-medium ${Object.keys(quizAnswers).length < quiz.length ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-purple-600 text-white hover:bg-purple-700"}`} data-testid="submit-quiz-btn">
                {t("submit")} ({Object.keys(quizAnswers).length}/{quiz.length})
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  // Progress Page
  const ProgressPage = () => (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="progress-page" dir={isRTL ? "rtl" : "ltr"}>
      <h1 className="text-3xl font-bold mb-8">{t("my_progress")}</h1>

      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center space-x-3 mb-2"><BookOpen className="w-6 h-6 text-purple-600" /><span className="text-gray-600">{t("modules_completed")}</span></div>
          <div className="text-3xl font-bold">{formatNumber(userProgress.filter(p => p.passed).length)} / {formatNumber(modules.length)}</div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center space-x-3 mb-2"><CheckCircle className="w-6 h-6 text-green-600" /><span className="text-gray-600">{t("lessons_completed")}</span></div>
          <div className="text-3xl font-bold">{formatNumber(lessonProgress.filter(p => p.completed).length)}</div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center space-x-3 mb-2"><Award className="w-6 h-6 text-yellow-600" /><span className="text-gray-600">{t("certificates_obtained")}</span></div>
          <div className="text-3xl font-bold">{formatNumber(certificates.length)}</div>
        </div>
      </div>

      {certificates.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-bold mb-4">{t("my_certificates")}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {certificates.map((cert) => {
              const module = modules.find(m => m.id === cert.module_id);
              return (
                <div 
                  key={cert.id} 
                  className="bg-gradient-to-r from-yellow-100 to-yellow-50 rounded-xl border border-yellow-200 p-4 cursor-pointer hover:shadow-lg transition" 
                  onClick={() => setShowCertificate({ certificate: cert, module })}
                  data-testid={`certificate-${cert.id}`}
                >
                  <div className="flex items-center space-x-3 mb-2">
                    <Award className="w-8 h-8 text-yellow-600" />
                    <div>
                      <h3 className="font-semibold">{module?.title || "Module"}</h3>
                      <p className="text-sm text-gray-500">#{cert.certificate_number}</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">{t("delivered_on")} {new Date(cert.issued_at).toLocaleDateString()}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <h2 className="text-xl font-bold mb-4">{t("progress_by_module")}</h2>
      <div className="space-y-4">
        {modules.map((module) => {
          const Icon = IconMap[module.icon] || Brain;
          const progress = getModuleProgress(module.id);
          const passed = isModulePassed(module.id);
          const moduleProgress = userProgress.find(p => p.module_id === module.id);

          return (
            <div key={module.id} className="bg-white rounded-xl shadow-sm border p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${ColorBg[module.color]}`}><Icon className="w-5 h-5" /></div>
                  <div>
                    <h3 className="font-semibold">{module.title}</h3>
                    <p className="text-sm text-gray-500">{formatNumber(module.lesson_count)} {t("lessons")}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {passed && (
                    <span className="px-2 py-1 bg-green-100 text-green-600 text-xs rounded-full flex items-center">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Quiz: {formatNumber(moduleProgress?.quiz_score)}/{formatNumber(moduleProgress?.quiz_total)}
                    </span>
                  )}
                  <span className="text-sm font-medium text-gray-600">{formatNumber(progress)}%</span>
                </div>
              </div>
              <div className="h-2 bg-gray-200 rounded-full">
                <div className={`h-2 rounded-full bg-gradient-to-r ${ColorMap[module.color]} transition-all`} style={{ width: `${progress}%` }}></div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // Footer
  const Footer = () => (
    <footer className="bg-gray-900 text-white py-12" dir={isRTL ? "rtl" : "ltr"}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-center mb-6">
          <div className="flex items-center mb-4 md:mb-0">
            <img 
              src="https://customer-assets.emergentagent.com/job_french-lexilearn/artifacts/ftk31l6l_ChatGPT%20Image%205%20janv.%202026%2C%2022_55_05.png" 
              alt={t("app_title")} 
              className="w-10 h-10 rounded-lg object-cover"
            />
            <span className={`${isRTL ? 'mr-2' : 'ml-2'} text-xl font-bold`}>{t("app_title")}</span>
          </div>
          <div className="flex items-center space-x-2 text-gray-300">
            <span>{t("contact")}:</span>
            <a href="mailto:contact@mhpsslearning.org" className="text-purple-400 hover:text-purple-300 transition-colors">
              contact@mhpsslearning.org
            </a>
          </div>
        </div>
        <div className="border-t border-gray-800 pt-6 text-center">
          <p className="text-gray-400 text-sm">© {new Date().getFullYear()} {t("app_title")}. {t("app_subtitle")}</p>
        </div>
      </div>
    </footer>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      {showAuth && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="auth-modal">
          <div className="bg-white rounded-2xl p-8 w-full max-w-lg shadow-2xl">
            <h2 className="text-2xl font-bold mb-2 text-gray-800">{t("welcome")}</h2>
            <p className="text-gray-600 mb-6">{t("welcome_desc")}</p>
            <form onSubmit={handleLogin}>
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">{t("name")}</label>
                <input 
                  type="text" 
                  value={authName} 
                  onChange={(e) => setAuthName(e.target.value)} 
                  placeholder={t("name_placeholder")}
                  className="w-full px-4 py-4 text-base border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 placeholder-gray-400" 
                  required 
                  data-testid="auth-name" 
                  autoComplete="off"
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">{t("email")}</label>
                <input 
                  type="text" 
                  value={authEmail} 
                  onChange={(e) => setAuthEmail(e.target.value)} 
                  placeholder={t("email_placeholder")}
                  className="w-full px-4 py-4 text-base border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 placeholder-gray-400" 
                  required 
                  data-testid="auth-email" 
                  autoComplete="off"
                />
              </div>
              <div className="flex space-x-3">
                <button type="button" onClick={() => setShowAuth(false)} className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl hover:bg-gray-50 font-medium transition-all duration-200">{t("cancel")}</button>
                <button type="submit" className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 font-medium transition-all duration-200 shadow-lg shadow-purple-200" data-testid="auth-submit">{t("continue_btn")}</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showCertificate && (
        <CertificateModal 
          certificate={showCertificate.certificate} 
          module={showCertificate.module} 
          user={user} 
          t={t} 
          onClose={() => setShowCertificate(null)} 
        />
      )}
      
      <main className="flex-1">
        {currentPage === "home" && <HomePage />}
        {currentPage === "modules" && <ModulesPage />}
        {currentPage === "lesson" && <LessonPage />}
        {currentPage === "quiz" && <QuizPage />}
        {currentPage === "progress" && <ProgressPage />}
      </main>

      <Footer />
    </div>
  );
}

export default App;
